import {
  fillEcosystemAddressesFromAccounts,
  RouteModel,
  webSupportedChainTypeKeys,
} from '@polaris/core/models'
import { initiateRoute } from '@polaris/core/services'
import { getEcosystemAddresses } from '@polaris/core/signing'
import { featureFlagsAtom } from '@polaris/core/stores'
import { getInsufficientFeeInfo, limitDecimals } from '@polaris/core/utils'
import {
  RESET,
  useFieldActions,
  useFieldValue,
  useForm,
  useFormActions,
  useFormStatus,
} from 'form-atoms'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { usePathname, useRouter } from 'next/navigation'
import { useQueryStates } from 'nuqs'
import { useCallback, useEffect, useRef } from 'react'
import { errorToast } from '@/src/components/ui/toast'
import { useTokenActions } from '@/src/hooks/token-info/use-token-actions'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { useFilteredBalances } from '@/src/queries/hooks/balances'
import { mpcWalletAddressesWithNamadaOverrideAtom } from '@/src/stores/mpc/atoms'
import {
  walletKeyringAtom,
  walletOptionsAtom,
} from '@/src/stores/wallets/atoms'
import { isFeeToken as isFeeTokenUtil } from '@/src/utils/token-utils'
import { getCurrentPageType } from '@/src/utils/url'
import { walletAccountsAtom } from '../accounts/wallets'
import { mpcNamadaKeysAtom } from '../mpc/atoms'
import { fromSearchParamsParsers, toSearchParamsParsers } from '../params'
import { useTrade } from '../route-machine/actions'

// Constants
const URL_UPDATE_DEBOUNCE_MS = 150 // Debounce delay for URL updates to prevent race conditions

import { accountsAtom } from '../accounts/atoms'
import {
  absoluteFundsRequiredAtom,
  clearManualInsufficientBalanceEffectAtom,
  finalSwapAddressOverwriteAtom,
  fromBalanceAtom,
  fromTokenAcrossChainsAtom,
  manualInsufficientBalanceForFeesErrorAtom,
  maxBalanceStateAtom,
  queueResetSubmitStatusAtom,
  submitStatusAtomEffect,
  swapFormAtom,
  swapHotkeyAtom,
  swapRouteAtom,
  toTokenAcrossChainsAtom,
} from './atoms'

export interface UseTokenFieldActionsProps {
  direction: 'from' | 'to'
}

export const useTokenFieldActions = (props: UseTokenFieldActionsProps) => {
  const { direction } = props
  const { fieldAtoms } = useForm(swapFormAtom)
  const { setValue: fieldFromSetValue } = useFieldActions(fieldAtoms.from)
  const { setValue: fieldToSetValue } = useFieldActions(fieldAtoms.to)
  const setMaxBalanceState = useSetAtom(maxBalanceStateAtom)
  const { connectedWallets, connectableWallets } =
    useAtomValue(walletOptionsAtom)
  const walletAccounts = useAtomValue(walletAccountsAtom)

  const [currentQueryParams, setQuery] = useQueryStates({
    ...fromSearchParamsParsers,
    ...toSearchParamsParsers,
  })

  // Handle wallet connection logic for the from asset
  const connectFromAccountWallet = useCallback(
    (address: string) => {
      const account = walletAccounts.accountsWithWallets.find(
        (account) => account.address === address,
      )
      if (
        account &&
        // Ensure wallet is not already connected, which means account needs to be switched and we can't do it automatically
        // This situatino is exposed via the swapRequirementsAtom
        (!connectedWallets[account.ecosystem] ||
          connectedWallets[account.ecosystem]?.displayName !==
            account.provider_info.name)
      ) {
        const walletToConnect = connectableWallets[account.ecosystem]?.find(
          (wallet) => wallet.displayName === account.provider_info.name,
        )
        if (walletToConnect) {
          walletToConnect.connect()
        }
      }
    },
    [walletAccounts, connectedWallets, connectableWallets],
  )

  const setValue = useCallback(
    ({
      denom,
      chain_id: chainId,
      address,
    }: Partial<{ chain_id: string; denom: string; address: string }>) => {
      const queryParams: {
        fromChain?: string | null
        fromToken?: string | null
        toChain?: string | null
        toToken?: string | null
      } = { ...currentQueryParams }

      if (direction === 'from') {
        queryParams.fromChain = chainId
        queryParams.fromToken = denom

        // Initiate wallet connection automatically if possible
        if (address) {
          connectFromAccountWallet(address)
        }

        fieldFromSetValue({
          token: denom,
          chain: chainId,
          address,
        })

        setMaxBalanceState('default')
      } else {
        queryParams.toChain = chainId
        queryParams.toToken = denom

        fieldToSetValue({
          token: denom,
          chain: chainId,
        })
      }

      // Avoid sending undefined values to the query params
      // nuqs won't set it only if it's null, not undefined
      for (const key in queryParams) {
        if (!queryParams[key as keyof typeof queryParams]) {
          queryParams[key as keyof typeof queryParams] = null
        }
      }

      setQuery(queryParams)
    },
    [
      direction,
      currentQueryParams,
      connectFromAccountWallet,
      setMaxBalanceState,
      setQuery,
      fieldFromSetValue,
      fieldToSetValue,
    ],
  )

  const setValueSwap = useCallback(
    ({
      from,
      to,
    }: {
      from: Partial<{ chain_id: string; denom: string; address: string }>
      to: Partial<{ chain_id: string; denom: string }>
    }) => {
      const queryParams: {
        fromChain?: string | null
        fromToken?: string | null
        toChain?: string | null
        toToken?: string | null
      } = { ...currentQueryParams }

      // Update query params for both directions
      queryParams.fromChain = from.chain_id
      queryParams.fromToken = from.denom
      queryParams.toChain = to.chain_id
      queryParams.toToken = to.denom

      // Handle wallet connection logic for the from asset
      if (from.address) {
        connectFromAccountWallet(from.address)
      }

      // Update field values
      fieldFromSetValue({
        token: from.denom,
        chain: from.chain_id,
        address: from.address,
      })

      fieldToSetValue({
        token: to.denom,
        chain: to.chain_id,
      })

      setMaxBalanceState('default')

      // Avoid sending undefined values to the query params
      // nuqs won't set it only if it's null, not undefined
      for (const key in queryParams) {
        if (!queryParams[key as keyof typeof queryParams]) {
          queryParams[key as keyof typeof queryParams] = null
        }
      }

      setQuery(queryParams)
    },
    [
      currentQueryParams,
      connectFromAccountWallet,
      setMaxBalanceState,
      setQuery,
      fieldFromSetValue,
      fieldToSetValue,
    ],
  )

  return {
    setValue,
    setValueSwap,
  }
}

export const useSwapFormActions = () => {
  const { fieldAtoms } = useForm(swapFormAtom)
  const { setValue: setAmountValue } = useFieldActions(fieldAtoms.fromAmount)
  const fromAmount = useFieldValue(fieldAtoms.fromAmount)
  const fromValue = useFieldValue(fieldAtoms.from)
  const toValue = useFieldValue(fieldAtoms.to)
  const { setValueSwap } = useTokenFieldActions({ direction: 'from' })
  const fromBalance = useAtomValue(fromBalanceAtom)
  const absoluteFundsRequired = useAtomValue(absoluteFundsRequiredAtom)
  const [maxBalanceState, setMaxBalanceState] = useAtom(maxBalanceStateAtom)
  const swapRoute = useAtomValue(swapRouteAtom)
  const { data: fromTokenData } = useAtomValue(fromTokenAcrossChainsAtom)

  // Get balance data to check if there are other tokens on the chain
  const { balancesByChains } = useFilteredBalances({
    chainId: fromTokenData?.requested_asset.chain_id,
  })
  const hasOtherChainBalances = balancesByChains[0]?.balances.length > 1

  const setManualInsufficientBalanceForFeesError = useSetAtom(
    manualInsufficientBalanceForFeesErrorAtom,
  )
  const pathname = usePathname()
  const router = useRouter()
  const { isSwitchDisabled } = useTokenActions()
  const { data: toTokenData } = useAtomValue(toTokenAcrossChainsAtom)

  // URL update queue - prevents race conditions from rapid clicks
  const urlUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Track the last programmatically set value to distinguish from manual user input
  const lastSetAmountRef = useRef<string | null>(null)

  // Store first click value for Type C to calculate second click
  const firstClickValueRef = useRef<number | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current)
      }
    }
  }, [])

  useAtom(clearManualInsufficientBalanceEffectAtom)

  const setFromBalance = useCallback(() => {
    const rawBalance = fromBalance?.normalized_amount
    if (!rawBalance) return

    setManualInsufficientBalanceForFeesError(undefined)

    // Get fee requirements
    const costCheckResult =
      swapRoute.data?.route.FeeRequirements?.cost_check_result
    const fromToken = fromTokenData?.requested_asset

    // Check if this is a fee token
    let isFeeToken = false
    if (costCheckResult && fromToken) {
      isFeeToken = !!(
        costCheckResult.fee.denom?.toLowerCase() ===
          fromToken.denom?.toLowerCase() ||
        costCheckResult.fee.ticker?.toLowerCase() ===
          fromToken.ticker?.toLowerCase()
      )
    }

    // Fallback: Use token metadata to determine if it's a native fee token
    if (!isFeeToken && fromToken) {
      isFeeToken = isFeeTokenUtil(fromToken)
    }

    // Type C: Fee token WITH other balances on same chain
    const isTypeC = isFeeToken && hasOtherChainBalances

    // ========================================
    // TYPE C: Three-state cycle (default → first-click → second-click → default)
    // ========================================
    if (isTypeC) {
      // Block if route is loading
      if (!costCheckResult) {
        return
      }

      // Get Type C calculation values
      const feesWithSafety = costCheckResult.funds_required_display?.native || 0
      const dustAmount = costCheckResult.follow_on_dust_amount?.native || 0
      // Gas station funding amount needs to be reserved from the source chain (e.g., NAM -> OSMO conversion)
      const gasStationSourceChainAmount =
        swapRoute.data?.route.FeeRequirements?.gas_station
          ?.source_chain_native_amount || 0

      // Calculate max safe tradeable amount using absoluteFundsRequired if available
      let maxAmount: number
      if (absoluteFundsRequired) {
        maxAmount = Math.min(rawBalance, absoluteFundsRequired)
      } else {
        // Fallback: Reserve display fees + gas station funding for source chain
        maxAmount = Math.max(
          0,
          rawBalance - feesWithSafety - gasStationSourceChainAmount,
        )
      }

      if (maxBalanceState === 'default') {
        // First click: Max - Fees - Dust
        const maxMinusDust = Math.max(0, maxAmount - dustAmount)
        const amountString = limitDecimals(maxMinusDust, 6)

        firstClickValueRef.current = maxMinusDust
        lastSetAmountRef.current = amountString
        setMaxBalanceState('first-click')
        setAmountValue(amountString)
      } else if (maxBalanceState === 'first-click') {
        // Second click: Add dust back (Max - Fees only)
        const maxWithoutDust = (firstClickValueRef.current || 0) + dustAmount
        const amountString = limitDecimals(maxWithoutDust, 6)

        lastSetAmountRef.current = amountString
        setMaxBalanceState('second-click')
        setAmountValue(amountString)
      } else {
        // Third click: Reset to 0
        lastSetAmountRef.current = '0'
        setMaxBalanceState('default')
        setAmountValue('0')
        firstClickValueRef.current = null
      }
    }
    // ========================================
    // TYPE A & B: Two-state cycle (default → first-click → default)
    // ========================================
    else {
      if (maxBalanceState === 'default') {
        // First click: Set to max
        let maxAmount: number

        if (isFeeToken) {
          // Type B: Fee token without other balances
          // Use absoluteFundsRequired if available (most accurate)
          // Otherwise use rawBalance and let route validate
          if (absoluteFundsRequired) {
            maxAmount = Math.min(rawBalance, absoluteFundsRequired)
          } else {
            // Don't use stale fees from costCheckResult - let route recalculate
            maxAmount = rawBalance
          }
        } else {
          // Type A: Non-fee token
          maxAmount = rawBalance
        }

        const amountString = limitDecimals(maxAmount, 6)
        lastSetAmountRef.current = amountString
        setMaxBalanceState('first-click')
        setAmountValue(amountString)
      } else {
        // Second click: Reset to 0
        lastSetAmountRef.current = '0'
        setMaxBalanceState('default')
        setAmountValue('0')
      }
    }
  }, [
    fromBalance?.normalized_amount,
    absoluteFundsRequired,
    setAmountValue,
    maxBalanceState,
    setMaxBalanceState,
    setManualInsufficientBalanceForFeesError,
    swapRoute.data?.route.FeeRequirements?.cost_check_result,
    swapRoute.data?.route.FeeRequirements?.gas_station
      ?.source_chain_native_amount,
    fromTokenData?.requested_asset,
    hasOtherChainBalances,
  ])

  // Universal reset logic: Reset state when input manually changed
  useEffect(() => {
    // If input is different from what we programmatically set, user changed it manually
    if (
      lastSetAmountRef.current !== null &&
      fromAmount !== lastSetAmountRef.current
    ) {
      // User manually changed the input → reset to default state
      setMaxBalanceState('default')
      lastSetAmountRef.current = null
      firstClickValueRef.current = null
    }
  }, [fromAmount, setMaxBalanceState])

  // Smart auto-adjust for Type B: Wait for route to calculate fees, then correct the amount
  useEffect(() => {
    const rawBalance = fromBalance?.normalized_amount
    if (!rawBalance) return

    // Only run in first-click state
    if (maxBalanceState !== 'first-click') return

    // Detect if this is a fee token (same logic as setFromBalance)
    const costCheckResult =
      swapRoute.data?.route.FeeRequirements?.cost_check_result
    const fromToken = fromTokenData?.requested_asset
    let isFeeToken = false

    if (costCheckResult && fromToken) {
      isFeeToken = !!(
        costCheckResult.fee.denom?.toLowerCase() ===
          fromToken.denom?.toLowerCase() ||
        costCheckResult.fee.ticker?.toLowerCase() ===
          fromToken.ticker?.toLowerCase()
      )
    }

    // Fallback: Use token metadata to determine if it's a native fee token
    if (!isFeeToken && fromToken) {
      isFeeToken = isFeeTokenUtil(fromToken)
    }

    // Only run for Type B (fee token WITHOUT other balances)
    const isTypeB = isFeeToken && !hasOtherChainBalances
    if (!isTypeB) return

    // Wait for absoluteFundsRequired to be available
    if (!absoluteFundsRequired) return

    // Check if we need to adjust
    const correctedAmount = Math.min(rawBalance, absoluteFundsRequired)
    const correctedAmountString = limitDecimals(correctedAmount, 6)

    // Only adjust if different from current input
    if (fromAmount !== correctedAmountString) {
      lastSetAmountRef.current = correctedAmountString
      setAmountValue(correctedAmountString)
    }
  }, [
    absoluteFundsRequired,
    maxBalanceState,
    fromAmount,
    fromBalance?.normalized_amount,
    hasOtherChainBalances,
    swapRoute.data?.route.FeeRequirements?.cost_check_result,
    fromTokenData?.requested_asset,
    setAmountValue,
  ])

  const prefetchSwitch = useCallback(() => {
    try {
      const isTokenPage = pathname.startsWith('/tokens')

      if (!isTokenPage) return

      const currentFromToken = fromTokenData?.requested_asset
      const currentToToken = toTokenData?.requested_asset

      if (currentFromToken?.ticker) {
        const newUrl = `/tokens?ticker=${currentFromToken.ticker}&toToken=${currentFromToken.denom}&toChain=${currentFromToken.chain_id}&fromToken=${currentToToken?.denom ?? ''}&fromChain=${currentToToken?.chain_id ?? ''}`
        router.prefetch(newUrl)
      }
    } catch (error) {
      console.error('Failed to prefetch swap route:', error)
      // Silent fail - prefetch is performance optimization, not critical
    }
  }, [pathname, fromTokenData, toTokenData, router])

  const switchFromToTokens = useCallback(() => {
    const isTokenPage = pathname.startsWith('/tokens')

    // Get current token data before swapping
    const currentFromToken = fromTokenData?.requested_asset
    const currentToToken = toTokenData?.requested_asset

    // Swap input with output: Set input to previous output amount
    const outputAmount = swapRoute.data?.route.AmountOut
    if (outputAmount && outputAmount > 0) {
      const outputString = limitDecimals(outputAmount, 6)
      setAmountValue(outputString)
      // IMPORTANT: Set lastSetAmountRef to null so it's treated as user input
      // This allows it to work exactly like manual typing
      lastSetAmountRef.current = null
    } else {
      // Fallback if no route calculated yet
      setAmountValue(RESET)
      lastSetAmountRef.current = null
    }

    // Always reset max states (back to default, no "Using max" badges)
    setMaxBalanceState('default')
    firstClickValueRef.current = null

    // PHASE 1: Instant UI update - swap atoms immediately for instant visual feedback
    setValueSwap({
      from: {
        chain_id: toValue?.chain ?? undefined,
        denom: toValue?.token ?? undefined,
      },
      to: {
        chain_id: fromValue?.chain ?? undefined,
        denom: fromValue?.token ?? undefined,
      },
    })

    // PHASE 2: Queue URL update with debounce (only for token pages)
    if (isTokenPage) {
      // Cancel any pending URL update
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current)
      }

      // Queue new URL update after debounce settle time
      urlUpdateTimeoutRef.current = setTimeout(() => {
        try {
          // Extract current ticker from search params
          const searchParams = new URLSearchParams(window.location.search)
          const currentTicker = searchParams.get('ticker')

          if (!currentTicker) return

          // Build URL with same ticker but swapped FROM/TO query params
          const newUrl = `/tokens?ticker=${currentTicker}&toToken=${currentFromToken?.denom ?? ''}&toChain=${currentFromToken?.chain_id ?? ''}&fromToken=${currentToToken?.denom ?? ''}&fromChain=${currentToToken?.chain_id ?? ''}`

          // Apply URL update after debounce settles
          router.replace(newUrl)
        } catch (error) {
          console.error('Failed to update URL after swap:', error)
          // Silent fail - atoms already updated, URL update is non-critical
        }
      }, URL_UPDATE_DEBOUNCE_MS)
    }
  }, [
    fromValue,
    toValue,
    setAmountValue,
    pathname,
    fromTokenData,
    toTokenData,
    router,
    setValueSwap,
    setMaxBalanceState,
    swapRoute.data?.route.AmountOut,
  ])

  return {
    setFromBalance,
    switchFromToTokens,
    prefetchSwitch,
    isSwitchDisabled,
  }
}

/** Reset form state, but avoid setting tokens to initially hydrated values. */
export const useSwapFormAmountReset = () => {
  const { fieldAtoms } = useForm(swapFormAtom)
  const { reset: resetForm } = useFormActions(swapFormAtom)

  const from = useFieldValue(fieldAtoms.from)
  const to = useFieldValue(fieldAtoms.to)
  const { setValue: setFromValue } = useFieldActions(fieldAtoms.from)
  const { setValue: setToValue } = useFieldActions(fieldAtoms.to)

  const reset = useCallback(() => {
    resetForm()
    setFromValue(from)
    setToValue(to)
  }, [resetForm, setFromValue, setToValue, from, to])

  return { reset }
}

export const useSwapFormSubmit = () => {
  const { ampli } = useAmplitudeAnalytics()
  const { submit } = useFormActions(swapFormAtom)
  const { reset } = useSwapFormAmountReset()
  const { submitStatus } = useFormStatus(swapFormAtom)
  const { data } = useAtomValue(swapRouteAtom)
  const accounts = useAtomValue(accountsAtom)
  const walletKeyring = useAtomValue(walletKeyringAtom)
  const swapHotkey = useAtomValue(swapHotkeyAtom)
  const { privacy } = useAtomValue(featureFlagsAtom)
  const { data: mpcWalletAddresses } = useAtomValue(
    mpcWalletAddressesWithNamadaOverrideAtom,
  )
  const { data: mpcNamadaKeys } = useAtomValue(mpcNamadaKeysAtom)
  const fromTokenData = useAtomValue(fromTokenAcrossChainsAtom)
  const setManualInsufficientBalanceForFeesError = useSetAtom(
    manualInsufficientBalanceForFeesErrorAtom,
  )
  const [, setQueueResetSubmitStatus] = useAtom(queueResetSubmitStatusAtom)
  useAtom(submitStatusAtomEffect)
  const finalSwapAddressOverwrite = useAtomValue(finalSwapAddressOverwriteAtom)
  // Want to use this^, however it gets converted to chain address (i.e. celestia) but the accounts store cosmos addresses as cosmos hub addresses
  // Use toWalletAddressAtom and pass to fillEcosystemAddressesFromAccounts when selecting accounts
  const { start } = useTrade()

  const onSubmit = useCallback(
    () =>
      submit(async (values) => {
        try {
          if (!data?.route) throw new Error('No route data')
          const route = new RouteModel(data.route)
          const toToken = values.to
          const fromToken = values.from

          const ecosystemToAddrMain = swapHotkey
            ? getEcosystemAddresses(swapHotkey)
            : getEcosystemAddresses(walletKeyring)

          const fromChainType = fromTokenData?.data?.requested_asset.chain_type
          // TODO: refactor function toprefer selected destination account in finalDest keyring
          const ecosystemToAddrFinalDest = fillEcosystemAddressesFromAccounts(
            accounts,
            walletKeyring,
            webSupportedChainTypeKeys,
            fromChainType,
          )

          if (privacy) {
            ecosystemToAddrFinalDest.namada = mpcWalletAddresses?.namada

            // Add pseudo extended key for privacy routes
            if (mpcNamadaKeys?.pseudoExtendedKey) {
              ecosystemToAddrFinalDest['namada:pseudo-extended-key'] =
                mpcNamadaKeys.pseudoExtendedKey
            }

            if (mpcNamadaKeys?.shieldedAddress) {
              ecosystemToAddrFinalDest['namada:shielded-address'] =
                mpcNamadaKeys.shieldedAddress
            }
          }

          if (
            !mpcWalletAddresses &&
            toToken &&
            toToken?.chain !== fromToken?.chain
          ) {
            throw new Error('MPC wallet addresses are not defined')
          }

          if (
            !route.InputAssetMetadata.asset_key ||
            !route.OutputAssetMetadata.asset_key
          ) {
            throw new Error('Asset key is not defined')
          }

          // We should provide a warning if we're using MPC autopilot but no MPC wallet addresses are provided
          if (route.UsesMPCAutopilot && !mpcWalletAddresses) {
            console.warn(
              'Route expecting Autopilot but no MPC wallet addresses provided',
            )
          }

          // Initiate a route. The route is recomputed with the addresses
          // provided. The initiated route with UUID is returned.
          const routeWithAddresses = await initiateRoute(
            {
              fromChain: route.InputAssetMetadata.asset_key.chain_id,
              fromAsset: route.InputAssetMetadata.asset_key.denom,
              toChain: route.OutputAssetMetadata.asset_key.chain_id,
              toAsset: route.OutputAssetMetadata.asset_key.denom,
              amount: route.AmountIn.toString(),
              slippage: values.slippage,
            },
            {
              ecosystemToAddrMain: mpcWalletAddresses
                ? mpcWalletAddresses
                : ecosystemToAddrMain,
              finalSwapAddressOverwrite,
              ecosystemToAddrFinalDest,
            },
          )
          if (!routeWithAddresses) return

          if (
            !routeWithAddresses.FeeRequirements?.cost_check_result
              .has_sufficient_balance_to_fund
          ) {
            const insufficientFeeInfo =
              getInsufficientFeeInfo(routeWithAddresses)

            if (insufficientFeeInfo) {
              setManualInsufficientBalanceForFeesError(insufficientFeeInfo)
            } else {
              errorToast('Not enough funds for fees')
            }

            setQueueResetSubmitStatus(true)

            // Return since we already showed an error toast
            return reset()
          }

          ampli?.swapSwapStarted({
            feeValueUSD: route.Steps.map((step) => step.step_fee_in_usd).reduce(
              (fees, fee) => fees + fee,
              0,
            ),
            valueUSD: route.OutputAssetUSDPrice * route.AmountOut,
            fromToken: `${route.InputAssetMetadata.display_name}:${route.InputAssetMetadata.ticker}`,
            toToken: `${route.OutputAssetMetadata.display_name}:${route.OutputAssetMetadata.ticker}`,
            fromChain: route.Steps[0].from_chain.chain_name,
            toChain: route.Steps[route.Steps.length - 1].to_chain.chain_name,
            page: getCurrentPageType(),
            fromTokenAmount: String(route.AmountIn),
            toTokenAmount: String(route.AmountOut),
            routeID: route.ID || undefined,
          })

          start({
            route: routeWithAddresses,
          })
        } catch (error) {
          console.error('Failed to initiate route:', error)
          errorToast('Failed to initiate route')
          reset()
        }
      })(),
    [
      submit,
      data?.route,
      swapHotkey,
      accounts,
      walletKeyring,
      finalSwapAddressOverwrite,
      mpcWalletAddresses,
      mpcNamadaKeys?.pseudoExtendedKey,
      mpcNamadaKeys?.shieldedAddress,
      fromTokenData,
      ampli,
      privacy,
      start,
      setQueueResetSubmitStatus,
      reset,
      setManualInsufficientBalanceForFeesError,
    ],
  )

  return {
    onSubmit,
    isLoading: submitStatus !== 'idle',
  }
}
